import { t, Instance, flow } from 'mobx-state-tree';
import { StoryModel, createStoryModelFromResponse } from './models/Story';
import { storiesApi } from '@/api/storiesWrapper';
import { generateUUID } from '@/utils/uuid';
import type { CreateStoryPayload, UpdateStoryPayload } from '@/types/story';
import type { StoryResponse } from 'we-tree-api-client/v2';

async function syncStoryMedia(
  familyId: string,
  storyId: string,
  existing: { id: string; mediaAssetId: string }[],
  desiredAssetIds: string[]
): Promise<void> {
  const desiredSet = new Set(desiredAssetIds);
  const existingByAsset = new Map(existing.map((item) => [item.mediaAssetId, item]));

  for (const attachment of existing) {
    if (!desiredSet.has(attachment.mediaAssetId)) {
      await storiesApi.unlinkStoryMedia(familyId, storyId, attachment.id);
    }
  }

  for (const assetId of desiredAssetIds) {
    if (!existingByAsset.has(assetId)) {
      await storiesApi.linkStoryMedia(familyId, storyId, {
        id: generateUUID(),
        mediaAssetId: assetId,
        refRole: 'attachment'
      });
    }
  }
}

function upsertStoryInMemberMap(
  storiesByFamilyMember: Instance<typeof PersonStoriesStore>['storiesByFamilyMember'],
  familyMemberId: string,
  story: Instance<typeof StoryModel>
) {
  let storiesMap = storiesByFamilyMember.get(familyMemberId);
  if (!storiesMap) {
    storiesByFamilyMember.set(familyMemberId, {});
    storiesMap = storiesByFamilyMember.get(familyMemberId)!;
  }
  storiesMap.set(story.id, story);
}

export const PersonStoriesStore = t
  .model('PersonStoriesStore', {
    storiesByFamilyMember: t.map(t.map(StoryModel)),
    loadingStates: t.map(t.boolean),
    errors: t.map(t.string)
  })
  .views((self) => ({
    getStoriesForFamilyMember(familyMemberId: string): Instance<typeof StoryModel>[] {
      const storiesMap = self.storiesByFamilyMember.get(familyMemberId);
      if (!storiesMap) return [];
      return Array.from(storiesMap.values()).sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    },

    getStoryById(storyId: string): Instance<typeof StoryModel> | undefined {
      for (const storiesMap of self.storiesByFamilyMember.values()) {
        const story = storiesMap.get(storyId);
        if (story) return story;
      }
      return undefined;
    },

    isLoadingForFamilyMember(familyMemberId: string): boolean {
      return self.loadingStates.get(familyMemberId) || false;
    },

    getErrorForFamilyMember(familyMemberId: string): string | null {
      return self.errors.get(familyMemberId) || null;
    }
  }))
  .actions((self) => ({
    clearStoriesForFamilyMember(familyMemberId: string) {
      self.storiesByFamilyMember.delete(familyMemberId);
      self.loadingStates.delete(familyMemberId);
      self.errors.delete(familyMemberId);
    },

    clear() {
      self.storiesByFamilyMember.clear();
      self.loadingStates.clear();
      self.errors.clear();
    },

    setLoading(familyMemberId: string, isLoading: boolean) {
      if (isLoading) {
        self.loadingStates.set(familyMemberId, true);
      } else {
        self.loadingStates.delete(familyMemberId);
      }
    },

    setError(familyMemberId: string, errorMessage: string | null) {
      if (errorMessage) {
        self.errors.set(familyMemberId, errorMessage);
      } else {
        self.errors.delete(familyMemberId);
      }
    },

    loadStoriesForFamilyMember: flow(function* (familyId: string, familyMemberId: string) {
      self.loadingStates.set(familyMemberId, true);
      self.errors.delete(familyMemberId);

      try {
        const result = yield storiesApi.searchStories(familyId, {
          familyMemberId,
          page: 1,
          pageSize: 100,
          sortBy: 'createdAt',
          sortOrder: 'desc'
        });

        if (result.success && result.data) {
          let storiesMap = self.storiesByFamilyMember.get(familyMemberId);
          if (!storiesMap) {
            self.storiesByFamilyMember.set(familyMemberId, {});
            storiesMap = self.storiesByFamilyMember.get(familyMemberId)!;
          } else {
            storiesMap.clear();
          }

          (result.data.items || []).forEach((item: StoryResponse) => {
            try {
              const storyModel = createStoryModelFromResponse(item, false);
              storiesMap.set(item.id, storyModel);
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error('[PersonStoriesStore] Failed to create story model:', error);
            }
          });
        } else {
          const errorMessage = result.error?.getUserMessage() || 'Failed to load memories';
          self.errors.set(familyMemberId, errorMessage);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
        self.errors.set(familyMemberId, errorMessage);
      } finally {
        self.loadingStates.delete(familyMemberId);
      }
    }),

    loadStoryDetail: flow(function* (familyId: string, storyId: string) {
      try {
        const result = yield storiesApi.getStory(familyId, storyId);
        if (result.success && result.data) {
          const memberId = result.data.familyMemberId;
          if (memberId) {
            const storyModel = createStoryModelFromResponse(result.data, true);
            upsertStoryInMemberMap(self.storiesByFamilyMember, memberId, storyModel);
          }
          return { success: true, data: result.data };
        }
        return { success: false, error: result.error };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error : new Error('Failed to load memory')
        };
      }
    }),

    createStory: flow(function* (familyId: string, payload: CreateStoryPayload) {
      const storyId = generateUUID();
      try {
        const result = yield storiesApi.createStory(familyId, {
          id: storyId,
          familyMemberId: payload.familyMemberId,
          title: payload.title.trim(),
          content: payload.content.trim()
        });

        if (!result.success || !result.data) {
          return { success: false, error: result.error };
        }

        const assetIds = payload.mediaAssetIds ?? [];
        if (assetIds.length > 0) {
          yield syncStoryMedia(familyId, storyId, [], assetIds);
          const detailResult = yield storiesApi.getStory(familyId, storyId);
          if (detailResult.success && detailResult.data) {
            const storyModel = createStoryModelFromResponse(detailResult.data, true);
            upsertStoryInMemberMap(self.storiesByFamilyMember, payload.familyMemberId, storyModel);
            return { success: true, data: detailResult.data };
          }
        }

        const storyModel = createStoryModelFromResponse(result.data, true);
        upsertStoryInMemberMap(self.storiesByFamilyMember, payload.familyMemberId, storyModel);
        return { success: true, data: result.data };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error : new Error('Failed to create memory')
        };
      }
    }),

    updateStory: flow(function* (familyId: string, storyId: string, payload: UpdateStoryPayload) {
      try {
        const existing = self.getStoryById(storyId);
        const result = yield storiesApi.updateStory(familyId, storyId, {
          title: payload.title.trim(),
          content: payload.content.trim()
        });

        if (!result.success || !result.data) {
          return { success: false, error: result.error };
        }

        if (payload.mediaAssetIds) {
          let currentAttachments =
            existing?.mediaAttachments.map((a) => ({ id: a.id, mediaAssetId: a.mediaAssetId })) ?? [];
          if (!existing) {
            const detailResult = yield storiesApi.getStory(familyId, storyId);
            if (detailResult.success && detailResult.data?.mediaAttachments) {
              currentAttachments = detailResult.data.mediaAttachments.map(
                (a: { id: string; mediaAssetId: string }) => ({
                  id: a.id,
                  mediaAssetId: a.mediaAssetId
                })
              );
            }
          }
          yield syncStoryMedia(familyId, storyId, currentAttachments, payload.mediaAssetIds);
        }

        const detailResult = yield storiesApi.getStory(familyId, storyId);
        const response = detailResult.success && detailResult.data ? detailResult.data : result.data;
        const memberId = response.familyMemberId ?? existing?.familyMemberId;
        if (memberId) {
          const storyModel = createStoryModelFromResponse(response, true);
          upsertStoryInMemberMap(self.storiesByFamilyMember, memberId, storyModel);
        }

        return { success: true, data: response };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error : new Error('Failed to update memory')
        };
      }
    }),

    deleteStory: flow(function* (familyId: string, storyId: string) {
      try {
        const story = self.getStoryById(storyId);
        if (!story) {
          return { success: false, error: new Error('Memory not found in store') };
        }

        const result = yield storiesApi.deleteStory(familyId, storyId);
        if (result.success) {
          const memberId = story.familyMemberId;
          if (memberId) {
            const storiesMap = self.storiesByFamilyMember.get(memberId);
            storiesMap?.delete(storyId);
          }
          return { success: true };
        }
        return { success: false, error: result.error };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error : new Error('Failed to delete memory')
        };
      }
    })
  }));

export type PersonStoriesStoreType = Instance<typeof PersonStoriesStore>;

export const personStoriesStore = PersonStoriesStore.create({});
